import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { searchContext } from '../../../../context/Context';
import { DATASETS } from '../../../Constants';
import updateParams from '../../../../utils/updateParams';
import dropdownLists from '../../DropdownLists';
import { variantLink } from '../renders/MappingFunctions';
import {
  renderAlleleDataVDA,
  renderAlleleDataVariant,
  renderVariantLink,
  renderRoundedValues,
} from '../renders/CommonAtributes';
import { OpenPopup } from '../components';
import { getHeaderProps } from '../config/helpers';
import { createSelectFilter } from '../filters/filterConfigs';
import { ColumnDefinition } from '../shared/column-types';

export const getVariantColumns = (context: any): ColumnDefinition[] => {
  const { tab } = context;
  const headerProps = getHeaderProps();

  const columns = [
    {
      name: 'exome',
      category: 'Variant',
      sortable: true,
      header: <span title="Allele Frequency in GNOMAD Exomes">AF EXOME</span>,
      uniqueID: 'AFExome',
      headerProps,
      render: row => {
        const { tab } = searchContext();
        if (tab === 'VARIANTS') {
          return renderAlleleDataVariant(row, 'exome');
        } else if (tab === 'VDA') {
          return renderAlleleDataVDA(row, 'exome');
        }
      },
    },

    {
      name: 'alleleFreqExome',
      category: 'Variant',
      sortable: true,
      header: (
        <>
          <span title="Allele Frequency in GNOMAD Exomes">AF EXOME</span>{' '}
          <OpenPopup popupType="showGenomeExome" />
        </>
      ),
      headerProps,
      render: row => {
        const { tab } = searchContext();
        if (tab === 'VARIANTS') {
          return renderAlleleDataVariant(row, 'alleleFreqExome');
        } else if (tab === 'VDA' || tab === 'DDA') {
          return renderAlleleDataVDA(row, 'alleleFreqExome');
        }
      },
      minWidth: 115,
    },

    {
      name: 'genome',
      category: 'Variant',
      sortable: true,
      header: <span title="Allele Frequency in GNOMAD Genomes">AF GENOME</span>,
      uniqueID: 'AFGenome',
      headerProps,
      render: instance => renderAlleleDataVariant(instance, 'genome'),
    },

    {
      name: 'alleleFreqGenome',
      category: 'Variant',
      sortable: true,
      header: (
        <>
          <span title="Allele Frequency in GNOMAD Genomes">AF GENOME</span>{' '}
          <OpenPopup popupType="showGenomeExome" />
        </>
      ),
      headerProps,
      render: row => {
        const { tab } = searchContext();
        if (tab === 'VARIANTS') {
          return renderAlleleDataVariant(row, 'alleleFreqGenome');
        } else if (tab === 'VDA' || tab === 'DDA') {
          return renderAlleleDataVDA(row, 'alleleFreqGenome');
        }
      },
      minWidth: 115,
    },

    {
      name: 'alleles',
      category: 'Variant',
      sortable: true,
      header: (
        <>
          <span title="Reference/Alternative Allele">Alleles</span> 
          <OpenPopup popupType="showAlleles" />
        </>
      ),
      headerProps,
      minWidth: 115,
      render: ({ data }) => {
        const { tab } = searchContext();
        let alleles = '';
        
        if (tab === 'VARIANTS') {
          const allelesArray = data.alleles?.map(value => `${value.ref}\\${value.alt}`);
          alleles = allelesArray ? allelesArray.join('; ') : '';
        } else if (tab === 'VDA' || tab === 'DDA') {
          const ref = data.alleles_ref;
          const alt = data.alleles_alt;
          alleles = `${ref}/${alt}`;
        }
        
        return <span>{alleles}</span>;
      },
    },

    {
      name: 'chromosome',
      category: 'Variant',
      sortable: true,
      header: (
        <>
          <span title="Chromosome">Chr</span> 
          <OpenPopup popupType="showVariantPosition" />
        </>
      ),
      headerProps,
      minWidth: 130,
    },

    {
      name: 'coord',
      category: 'Variant',
      sortable: true,
      header: (
        <>
          <span title="Position in the Chromosome">Position</span>{' '}
          <OpenPopup popupType="showVariantPosition" />
        </>
      ),
      headerProps,
      minWidth: 150,
    },

    {
      name: 'DBSNPclass',
      category: 'Variant',
      sortable: true,
      header: (
        <>
          <span title="Class of variant">Variant type</span> 
          <OpenPopup popupType="showVariantType" />
        </>
      ),
      minWidth: 150,
      uniqueID: 'DBSNPClass',
      headerProps,
      ...createSelectFilter(dropdownLists.DBSNPclass),
      render: ({ data }) => {
        const { tab } = searchContext();
        return tab === 'VARIANTS' ? data.dbnspClass : data.DBSNPclass;
      },
    },

    {
      name: 'polyphen_score',
      category: 'Variant',
      sortable: true,
      header: <span title="PolyPhen">PolyPhen</span>,
      headerProps,
      render: instance => renderRoundedValues(instance, 'polyphen_score'),
      minWidth: 85,
    },

    {
      name: 'sift_score',
      category: 'Variant',
      sortable: true,
      header: <span title="SIFT">SIFT</span>,
      headerProps,
      render: instance => renderRoundedValues(instance, 'sift_score'),
      minWidth: 85,
    },

    {
      name: 'mostSevereConsequences',
      category: 'Variant',
      sortable: true,
      header: (
        <>
          <span title="Most Severe VEP Consequence Type">Consequence</span>
          <OpenPopup popupType="showVariantConsequence" />
        </>
      ),
      headerProps,
      ...createSelectFilter(dropdownLists.mostSevereConsequences),
    },

    {
      name: 'strID',
      category: 'Variant',
      sortable: true,
      header: <span title="Variant">Variant</span>,
      headerProps,
      minWidth: 130,
      render: row => {
        const variant = row.data.strID;
        return variant ? variantLink(variant) : '';
      },
    },

    {
      name: 'variantStrID',
      category: 'Variant',
      sortable: true,
      header: <span title="Variant">Variant</span>,
      headerProps,
      render: instance => renderVariantLink(instance),
    },

    {
      name: 'variantDPI',
      category: 'Variant',
      sortable: true,
      header: (
        <>
          <span title="Disease Pleiotropy Index for the variant">
            DPI<sub>v</sub>
          </span>
          <OpenPopup popupType="showDPI" />
        </>
      ),
      headerProps,
      minWidth: 180,
    },

    {
      name: 'variantDSI',
      category: 'Variant',
      sortable: true,
      header: (
        <>
          <span title="Disease Specificity Index for the variant">
            DSI<sub>v</sub>
          </span>
          <OpenPopup popupType="showDSI" />
        </>
      ),
      headerProps,
      minWidth: 200,
    },

    {
      name: 'numDiseasesAssociatedToVariant',
      category: 'Variant',
      sortable: true,
      header: (
        <span title="Number of diseases associated to the variant">
          N diseases<sub>v</sub>
        </span>
      ),
      headerProps,
      minWidth: 115,
      render: ({ data }) => {
        const { tab } = searchContext();
        const location = useLocation();
        let newParams = '';

        try {
          if (tab === 'VARIANTS') {
            newParams = updateParams({
              view: DATASETS.VARIANTS,
              tab: 'VDA',
              searchItems: [{ id: data.strID }],
            });
          } else if (tab === 'VDA') {
            newParams = updateParams({
              view: DATASETS.VARIANTS,
              tab: 'VDA',
              searchItems: [{ id: data.variantStrID }],
            });
          } else if (tab === 'DDA') {
            newParams = updateParams({
              view: DATASETS.DDA,
              tab: 'DDA',
              searchItems: [{ id: data.variantStrID }],
            });
          } else {
            return null;
          }
          
          const newUrl = `${location.pathname}?${newParams}`;
          return (
            <Link className="tab-links" to={newUrl}>
              {data.numDiseasesAssociatedToVariant}{' '}
            </Link>
          );
        } catch (error) {
          // Silent error handling - return null for failed URL updates
          return null;
        }
      },
    },

    {
      name: 'DPI',
      category: tab === 'VARIANTS' ? 'Variant' : 'Gene',
      sortable: true,
      header: (
        <>
          <span title="Disease Pleiotropy Index for the gene">
            DPI<sub>{tab === 'VARIANTS' ? 'v' : 'g'}</sub>
          </span>
          <OpenPopup popupType="showDPI" />
        </>
      ),
      headerProps,
      render: instance => renderRoundedValues(instance, 'DPI'),
      minWidth: 85,
    },

    {
      name: 'DSI',
      category: tab === 'VARIANTS' ? 'Variant' : 'Gene',
      sortable: true,
      header: (
        <>
          <span title="Disease Specificity Index for the gene">
            DSI<sub>{tab === 'VARIANTS' ? 'v' : 'g'}</sub>
          </span>
          <OpenPopup popupType="showDSI" />
        </>
      ),
      headerProps,
      render: instance => renderRoundedValues(instance, 'DSI'),
      minWidth: 85,
    },
  ];

  return columns;
};